import { defineStore } from 'pinia'

type InstallShaft = {
  shaftId: number
  shaftName: string
  position?: string
}

type InstallBuilding = {
  regionId: number
  regionName: string
  buildingId: number
  buildingName: string
  shafts: InstallShaft[]
}

type InstallContext = {
  token: string
  regionId: number
  tenantId: number
  tenantName: string
  regionName: string
  buildings: InstallBuilding[]
}

type InstallSelection = {
  terminalId: number | null
  terminalCode: string
  buildingId: number | null
  buildingName: string
  shaftId: number | null
  shaftName: string
  deviceType: string
  installPosition: string
}

const INSTALL_CONTEXT_KEY = 'lift-install-context'
const INSTALL_SELECTION_KEY = 'lift-install-selection'

const readStorage = <T>(key: string, fallback: T): T => {
  if (typeof window === 'undefined') {
    return fallback
  }

  const raw = window.sessionStorage.getItem(key)
  if (!raw) {
    return fallback
  }

  try {
    return JSON.parse(raw) as T
  } catch {
    window.sessionStorage.removeItem(key)
    return fallback
  }
}

export const useMainStore = defineStore('main', {
  state: () => ({
    count: 0,
    installContext: readStorage<InstallContext | null>(INSTALL_CONTEXT_KEY, null),
    installSelection: readStorage<InstallSelection>(INSTALL_SELECTION_KEY, {
      terminalId: null,
      terminalCode: '',
      buildingId: null,
      buildingName: '',
      shaftId: null,
      shaftName: '',
      deviceType: '智能屏',
      installPosition: '轿厢内'
    })
  }),
  actions: {
    increment() {
      this.count++
    },
    setInstallContext(context: InstallContext) {
      this.installContext = context
      window.sessionStorage.setItem(INSTALL_CONTEXT_KEY, JSON.stringify(context))
    },
    clearInstallContext() {
      this.installContext = null
      this.installSelection = {
        terminalId: null,
        terminalCode: '',
        buildingId: null,
        buildingName: '',
        shaftId: null,
        shaftName: '',
        deviceType: '智能屏',
        installPosition: '轿厢内'
      }
      window.sessionStorage.removeItem(INSTALL_CONTEXT_KEY)
      window.sessionStorage.removeItem(INSTALL_SELECTION_KEY)
    },
    setInstallSelection(selection: Partial<InstallSelection>) {
      this.installSelection = {
        ...this.installSelection,
        ...selection
      }
      window.sessionStorage.setItem(INSTALL_SELECTION_KEY, JSON.stringify(this.installSelection))
    }
  }
})

export type { InstallBuilding, InstallContext, InstallSelection, InstallShaft }
